import dayjs from 'dayjs';
import clsx from 'clsx';
import { AmountUtils, PaletteUtils } from '@shared/utils';
import { Tooltip } from '../tooltip';
import { ColorsEnum, Icon, NoData, Text } from '@shared/widgets';
import TransactionIcon from '@assets/icons/dashboard/transaction.svg';
import { ItemType } from '@shared/api/models';

export interface IProps {
  amount: number;
  numberOfTransaction: number;
  highestTransferAmount: {
    date: string;
    amount: number;
  }[];
  recurrentRecipientsInfo: {
    recipientName: string;
    amount: number;
    numberOfTransaction: number;
  }[];
  textValue: string;
  currency: string;
  itemType: ItemType;
}

export const TransactionItem = (props: IProps) => {
  const sortedValues = props?.highestTransferAmount ? [...props.highestTransferAmount] : [];
  const highestElement = sortedValues.sort((a, b) => b.amount - a.amount)?.[0]?.amount;

  return (
    <div className='flex gap-6 max-[1250px]:flex-wrap'>
      <div className='w-full bg-white dark:bg-soft-black pt-6 pl-6 relative'>
        <div className='max-w-48'>
          <Text className='mb-1 font-[Prata]' size='3xl' color={ColorsEnum.Primary}>
            {AmountUtils.transformToAmountWithCurrency(props.amount, props.currency)}
          </Text>
          <Text color={ColorsEnum.Secondary}>
            {props.itemType} amount of the account during {props.textValue}
          </Text>
        </div>
        <div className='mt-6 max-w-60'>
          <Text className='mb-1 font-[Prata]' size='3xl' color={ColorsEnum.Primary}>
            {props.numberOfTransaction}
          </Text>
          <Text color={ColorsEnum.Secondary}>
            Number of {props.itemType.toLowerCase()} transactions of the account during {props.textValue}
          </Text>
        </div>
        <Icon icon={<TransactionIcon />} className='flex justify-end w-full' color='primary' />
      </div>
      <div className='w-full bg-white dark:bg-soft-black p-6 flex flex-col justify-between'>
        <Text weight='semibold'>Top 5 days with the highest {props.itemType.toLowerCase()} transfer amount</Text>
        {props.highestTransferAmount?.length > 0 ? (
          <div className='mx-auto'>
            <div className='flex items-end gap-2 mb-5 mt-3'>
              {props.highestTransferAmount.map((item, idx) => (
                <Tooltip key={idx} content={AmountUtils.transformToAmountWithCurrency(item.amount, props.currency)} direction='right'>
                  <div className='flex flex-col'>
                    <div
                      className={clsx('max-h-[120px] w-12 rounded-md border border-primary', highestElement === item.amount && 'bg-primary')}
                      style={{ height: `calc(120px * ${(item.amount / highestElement).toFixed(3)})` }}
                    />
                    <Text className='text-center mt-2'>{dayjs(item.date).format('DD.MM')}</Text>
                  </div>
                </Tooltip>
              ))}
            </div>
          </div>
        ) : (
          <div className='my-auto'>
            <NoData text='No related records' />
          </div>
        )}
      </div>
      <div className='w-full bg-white dark:bg-soft-black p-6 pb-3 flex flex-col'>
        <Text weight='semibold'>Top 5 days recurrent {ItemType.Outgoing === props.itemType ? 'recipients' : 'senders'} with transferred amount</Text>
        {props.recurrentRecipientsInfo?.length > 0 ? (
          props.recurrentRecipientsInfo?.map((element, idx) => (
            <div key={idx} className='mt-3 flex py-1'>
              <div
                className={clsx(
                  'w-9 h-9 rounded-full flex items-center justify-center font-semibold text-base text-white',
                  PaletteUtils.isInsigniaPalette && 'bg-black',
                  PaletteUtils.isVcfcPalette && 'bg-primary',
                )}
              >
                {element.recipientName?.[0]}
              </div>
              <div className='ml-2'>
                <Text weight='semibold' color={PaletteUtils.getColor(undefined, ColorsEnum.Primary)}>
                  {element.recipientName}
                </Text>
                <div className='flex gap-2'>
                  <Text weight='semibold' size='xs' color={ColorsEnum.Secondary} isUppercase>
                    {AmountUtils.transformToAmountWithCurrency(element.amount, props.currency)}
                  </Text>
                  <Text weight='semibold' size='xs' color={ColorsEnum.Secondary} isUppercase>
                    {element.numberOfTransaction} transactions
                  </Text>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className='m-auto'>
            <NoData text='No related records' />
          </div>
        )}
      </div>
    </div>
  );
};
